import * as R from 'ramda';
import React from 'react';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { TextField } from 'Shared/form/TextField';
import { DatePickerField } from 'Shared/form/DatePickerField';
import { PhoneField } from 'App/components/PatientForm/components/PhoneField';
import { CheckboxField } from 'Shared/form/CheckboxField';
import SelectField from 'Shared/form/SelectField';
import { useFormikContext } from 'formik';
import {
    authorizedUnitsOfMeasureOptions,
    methodApprovalReceivedOptions,
    ndcSpecificApprovalOptions,
} from './constants';
import PropTypes from 'prop-types';
import { NumberField } from 'Shared/form/NumberField';
import { FaExclamationCircle } from 'react-icons/fa';
import CommonPatientFieldWrapper from 'App/components/PatientForm/components/CommonPatientFieldWrapper';
import RequiredTableHeaderLabel from 'Shared/form/RequiredTableHeaderLabel';

const propTypes = {
    isFormDisabled: PropTypes.bool.isRequired,
};

const defaultProps = {};

const tooltip = <Tooltip id="actions-tooltip">Must have an applicable protocol enabled.</Tooltip>;

function OverturnedForm({ isFormDisabled }) {
    const { values, setValues } = useFormikContext();

    return (
        <>
            <Row>
                <CommonPatientFieldWrapper fieldName="referralAppealPaNumber">
                    {(commonProps) => (
                        <Col md={2}>
                            <TextField
                                {...commonProps}
                                disabled={commonProps.disabled || isFormDisabled}
                                fieldName="details.paNumber"
                                label="PA Number"
                                placeholder="PA Number"
                            />
                        </Col>
                    )}
                </CommonPatientFieldWrapper>

                <CommonPatientFieldWrapper fieldName="referralAppealPaFollowUpPhoneNumber">
                    {(commonProps) => (
                        <Col md={2}>
                            <PhoneField
                                {...commonProps}
                                disabled={commonProps.disabled || isFormDisabled}
                                fieldName="details.followUpPhone"
                                label="PA Follow Up Phone Number"
                                placeholder="(###) ###-####"
                            />
                        </Col>
                    )}
                </CommonPatientFieldWrapper>
                <CommonPatientFieldWrapper fieldName="referralAppealEffectiveStartDate">
                    {(commonProps) => (
                        <Col md={2}>
                            <DatePickerField
                                {...commonProps}
                                disabled={commonProps.disabled || isFormDisabled}
                                label="Effective Start Date"
                                fieldName="details.effectiveStartDate"
                                required={commonProps.required}
                            />
                        </Col>
                    )}
                </CommonPatientFieldWrapper>
                <CommonPatientFieldWrapper fieldName="referralAppealEffectiveEndDate">
                    {(commonProps) => (
                        <Col md={2}>
                            <DatePickerField
                                {...commonProps}
                                disabled={commonProps.disabled || isFormDisabled}
                                label="Effective End Date"
                                fieldName="details.effectiveEndDate"
                                required={commonProps.required}
                            />
                        </Col>
                    )}
                </CommonPatientFieldWrapper>
            </Row>

            <Row>
                <Col md={8}>
                    <table className="therigy-table table-condensed">
                        <colgroup>
                            <col width="20%" />
                            <col width="20%" />
                            <col width="20%" />
                            <col width="20%" />
                            <col width="20%" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th id="medication-column">Medication</th>
                                <th id="authorized-quantity-column">
                                    <RequiredTableHeaderLabel
                                        label="Authorized Quantity"
                                        fieldName="referralAppealAuthorizedQuantity"
                                    />
                                </th>
                                <th id="unit-of-measure-column">
                                    <RequiredTableHeaderLabel
                                        label="Unit of Measure"
                                        fieldName="referralAppealUnitOfMeasure"
                                    />
                                </th>
                                <th id="ndc-specific-column">
                                    <RequiredTableHeaderLabel
                                        label="Is Approval NDC Specific?"
                                        fieldName="referralAppealIsApprovalNdcSpecific"
                                    />
                                </th>
                                <th id="approved-ndc-column">
                                    <RequiredTableHeaderLabel
                                        label="Approved NDC"
                                        fieldName="referralAppealApprovedNdc"
                                    />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {values.medications.map((medication, idx) => {
                                return (
                                    <tr key={medication.id}>
                                        <th style={{ verticalAlign: 'middle' }}>{medication.medication.name}</th>
                                        <td>
                                            <CommonPatientFieldWrapper fieldName="referralAppealAuthorizedQuantity">
                                                {(commonProps) => (
                                                    <NumberField
                                                        {...commonProps}
                                                        aria-labelledby="authorized-quantity-column"
                                                        min={0}
                                                        disabled={commonProps.disabled || isFormDisabled}
                                                        fieldName={`medications.${idx}.data.authorizedQuantity`}
                                                        placeholder="Authorized Quantity"
                                                    />
                                                )}
                                            </CommonPatientFieldWrapper>
                                        </td>
                                        <td>
                                            <CommonPatientFieldWrapper fieldName="referralAppealUnitOfMeasure">
                                                {(commonProps) => (
                                                    <SelectField
                                                        {...commonProps}
                                                        aria-labelledby="unit-of-measure-column"
                                                        isDisabled={commonProps.disabled || isFormDisabled}
                                                        isClearable
                                                        options={authorizedUnitsOfMeasureOptions}
                                                        fieldName={`medications.${idx}.data.authorizedUom`}
                                                    />
                                                )}
                                            </CommonPatientFieldWrapper>
                                        </td>
                                        <td>
                                            <CommonPatientFieldWrapper fieldName="referralAppealIsApprovalNdcSpecific">
                                                {(commonProps) => (
                                                    <SelectField
                                                        {...commonProps}
                                                        aria-labelledby="ndc-specific-column"
                                                        isDisabled={commonProps.disabled || isFormDisabled}
                                                        isClearable
                                                        options={ndcSpecificApprovalOptions}
                                                        fieldName={`medications.${idx}.data.ndcSpecificApproval`}
                                                    />
                                                )}
                                            </CommonPatientFieldWrapper>
                                        </td>
                                        <td>
                                            <CommonPatientFieldWrapper fieldName="referralAppealApprovedNdc">
                                                {(commonProps) => (
                                                    <TextField
                                                        {...commonProps}
                                                        aria-labelledby="approved-ndc-column"
                                                        disabled={commonProps.disabled || isFormDisabled}
                                                        fieldName={`medications.${idx}.data.approvedNdc`}
                                                        placeholder="Approved NDC"
                                                    />
                                                )}
                                            </CommonPatientFieldWrapper>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Col>
            </Row>

            <Row>
                <CommonPatientFieldWrapper fieldName="referralAppealHowWasApprovalReceived">
                    {(commonProps) => (
                        <Col md={4}>
                            <SelectField
                                {...commonProps}
                                isDisabled={commonProps.disabled || isFormDisabled}
                                fieldName="details.methodApprovalReceived"
                                options={methodApprovalReceivedOptions}
                                label="How was approval received?"
                            />
                        </Col>
                    )}
                </CommonPatientFieldWrapper>
            </Row>

            <Row>
                <CommonPatientFieldWrapper fieldName="referralAppealCreatePaRenewalActivity">
                    {(commonProps) => (
                        <Col md={6}>
                            <CheckboxField
                                {...commonProps}
                                disabled={commonProps.disabled || isFormDisabled}
                                fieldName="details.benefitRequiresReVerification"
                                label={
                                    <>
                                        Create a Prior Authorization Renewal activity{' '}
                                        <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={tooltip}>
                                            <FaExclamationCircle data-testid="icon" />
                                        </OverlayTrigger>
                                    </>
                                }
                                onChange={() => {
                                    R.compose(
                                        setValues,
                                        R.over(R.lensPath(['details', 'benefitRequiresReVerification']), R.not)
                                    )(values);
                                }}
                            />
                        </Col>
                    )}
                </CommonPatientFieldWrapper>
            </Row>
        </>
    );
}

OverturnedForm.propTypes = propTypes;
OverturnedForm.defaultProps = defaultProps;

export default OverturnedForm;
